export interface BaseResponse {
    success: 1 | 0;
    message?: string;
}

export enum ReportType {
    DETAILED_REPORT = 0,
    SUMMARY_REPORT = 1,
    GRAPH_REPORT = 2,
    DIMENSION_REPORT = 3
}

export enum FilterType {
    ROLE = 1,
    TEAM = 2
}

/**
 * Enum for all available graph types
 * Provides type safety for graph identities
 */
export enum GraphType {
    LINE_CHART = 'line-chart',
    STACKED_COLUMN_CHART = 'stacked-column-chart',
    STACKED_AREA_CHART = 'stacked-area-chart',
    RANGE_AREA_CHART = 'range-area-chart',
    NESTED_DONUT_CHART = 'nested-donut-chart',
    CLUSTERED_COLUMN_CHART = 'clustered-column-chart',
    PIE_CHART = 'pie-chart',
    SEMI_CIRCLE_PIE_CHART = 'semi-circle-pie-chart',
    DONUT_CHART = 'donut-chart',
    PYRAMID_CHART = 'pyramid-chart',
    BAR_CHART = 'bar-chart',
    COLUMN_CHART = 'column-chart',
    CURVED_COLUMN_CHART = 'curved-column-chart',
    SMOOTHED_LINE_CHART = 'smoothed-line-chart',
    STEP_LINE_CHART = 'step-line-chart',
    FUNNEL_CHART = 'funnel-chart',
    LOLLIPOP_CHART = 'lollipop-chart',
    DRAGGABLE_PIE_CHART = 'draggable-pie-chart',
    SPIRAL_BAR_TIMELINE_CHART = 'spiral-bar-timeline-chart',
    SPIRAL_TIMELINE_CHART = 'spiral-timeline-chart',
    FISHBONE_TIMELINE_CHART = 'fishbone-timeline-chart',
    SERPENTINE_TIMELINE_CHART = 'serpentine-timeline-chart',
    ROAD_TIMELINE_CHART = 'road-timeline-chart',
    LINEAR_TIMELINE_CHART = 'linear-timeline-chart'
}

/**
 * Enum for graph types based on functionality
 */
export enum GraphCategory {
    SINGLE_Y_AXIS = 1,
    MULTI_GRAPH = 2
}

/**
 * Interface for graph item structure
 */
export interface GraphItem {
    identity: GraphType;
    graph_type: GraphCategory;
    type: number;
    label: string;
    image: string;
    class?: string;
    styleClass?: string;
}

/**
 * Interface for graph menu structure
 */
export interface GraphMenu {
    label: string;
    items: GraphItem[];
}

/**
 * Constant array containing all available graph types
 * Useful for iteration and validation
 */
export const ALL_GRAPH_TYPES: GraphType[] = [
    GraphType.LINE_CHART,
    GraphType.STACKED_COLUMN_CHART,
    GraphType.STACKED_AREA_CHART,
    GraphType.RANGE_AREA_CHART,
    GraphType.NESTED_DONUT_CHART,
    GraphType.CLUSTERED_COLUMN_CHART,
    GraphType.PIE_CHART,
    GraphType.SEMI_CIRCLE_PIE_CHART,
    GraphType.DONUT_CHART,
    GraphType.PYRAMID_CHART,
    GraphType.BAR_CHART,
    GraphType.COLUMN_CHART,
    GraphType.CURVED_COLUMN_CHART,
    GraphType.SMOOTHED_LINE_CHART,
    GraphType.STEP_LINE_CHART,
    // GraphType.THREE_D_PIE_CHART,
    // GraphType.THREE_D_BAR_CHART,
    // GraphType.THREE_D_COLUMN_CHART,
    // GraphType.THREE_D_CYLINDER_CHART,
    GraphType.FUNNEL_CHART,
    GraphType.LOLLIPOP_CHART,
    GraphType.DRAGGABLE_PIE_CHART,
    GraphType.SPIRAL_BAR_TIMELINE_CHART,
    GraphType.SPIRAL_TIMELINE_CHART,
    GraphType.FISHBONE_TIMELINE_CHART,
    GraphType.SERPENTINE_TIMELINE_CHART,
    GraphType.ROAD_TIMELINE_CHART,
    GraphType.LINEAR_TIMELINE_CHART
];

/**
 * Helper function to check if a string is a valid graph type
 * @param value - The string to validate
 * @returns True if the string is a valid graph type
 */
export function isValidGraphType(value: string): value is GraphType {
    return ALL_GRAPH_TYPES.includes(value as GraphType);
}


export interface ExportResponse extends BaseResponse {
    fileName: string;
    reportUrl: string;
    exportType: ExportType;
    redirectUrl?: string;
    showExportProgress?: boolean;
}


export type ValidDataType = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12 | 13 | 14 | 15 | 16 | 17 | 18 | 19 | 20 | 21 | 22 | 23 | 28;

export type ExportType = 'excel' | 'google_sheet' | 'csv' | 'email' | 'schedule' | 'mobile' | 'png' | 'pdf' | 'svg' | 'jpg' | 'json' | 'xlsx' | 'html' | 'print' | '';

export interface ReportFilter {
    columnId: string | number;
    searchType: number;
    dataType: ValidDataType;
    value: string | number | null;
    startDate?: string | null;
    endDate?: string | null;
    condition?: string;
    [key: string]: any;
}

export interface SortColumn {
    code: string | number;
    name: string;
    sortOrder: string;
}

export interface ReportFormData {
    module?: number;
    reportId?: number;
    reportName?: string;
    reportDesc?: string;
    reportType?: ReportType;

    recordsPerPage?: number | null;

    businessUnitIds?: string[];

    columns?: (string | number)[];
    groupByFilters?: (string | number)[];
    groupByLevel?: number;

    dateFilterCol?: string | number | null;
    periodValue?: string | number | null;

    custStartDate?: string | number | any;
    custEndDate?: string | number | any;

    sortColumns?: SortColumn[];
    preDefinedFilters?: (number | null)[][];
    filters?: ReportFilter[];
    nullColumnsList?: (string | number)[];

    first?: number;
    rows?: number;
    /** Full result total (not page size) — used for export instant vs queue routing */
    recordsTotal?: number;

    preDefFilterIds?: number[];
    columnsList?: (string | number)[];
    orderByColumnsList?: Array<{ columnId: string | number; sortDir: string }>;
    groupByColumnsList?: (string | number)[];
    exportType?: ExportType;
    dimensionColumnId?: string | number;

    // Activity Reports
    filterType?: any;
    filterRole?: number | string;
    teamList?: string[];
    teamMembersList?: string[];
    roleUserIds?: string[];

    // Placement Report
    placementReportOn?: number[];
    currencyConversion?: boolean;
    currencyDetails?: number;
    instanceType?: number;

    // Graph Report
    getChartData?: 1 | 0;

    // Talent Bench Report
    marketingStatus?: number[];
    benchStatus?: number[];
    benchSubStatus?: number[];

    // Job Status Report
    includePipelines?: boolean;
    stages?: (string | number)[];
    usersList?: number[];

    // Default Graph Type
    defaultGraphType?: GraphType;
    drilldownReportInfo?: any;
    userReportId?: number;
    enableHierarchySecurity?: boolean;
    isAdminReport?: 0 | 1;
}
